import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import "./NavBar.css";
import {
  FaUserCircle,
  FaEnvelope,
  FaPencilAlt,
  FaSignOutAlt,
  FaQuestionCircle,
  FaBook,
  FaMapSigns,
} from "react-icons/fa";
import { useAuth } from "./AuthProvider";
import { useLiveStatus } from "./LiveStatusContext";
import { TRACK_EVENT_SELECTION_KEY } from "./TrackEventSelector";
import NotificationBell from "./NotificationBell";

const TABS = [
  { path: "/timeslip", label: "Slips" },
  { path: "/liveView", label: "Live" },
  { path: "/searchView", label: "Search" },
  { path: "/qualView", label: "Qual" },
  { path: "/watchlist", label: "Watch" },
];

const readSelection = () => {
  try {
    const raw = localStorage.getItem(TRACK_EVENT_SELECTION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const TOUR_KEY_BY_PATH = {
  '/timeslip': 'timeslip',
  '/liveView': 'live',
  '/searchView': 'search',

};

const CALLOUT_DISMISSED_KEY = "navCalloutDismissed";

const NavBar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const menuRef = useRef(null);
  const helpRef = useRef(null);
  const navRef = useRef(null);
  const chipRef = useRef(null);
  const [selection, setSelection] = useState(readSelection);
  const [calloutDismissed, setCalloutDismissed] = useState(
    () => !!localStorage.getItem(CALLOUT_DISMISSED_KEY)
  );

  const dismissCallout = () => {
    localStorage.setItem(CALLOUT_DISMISSED_KEY, "1");
    setCalloutDismissed(true);
  };

  const { user, logout } = useAuth();
  const { connectionStatus } = useLiveStatus();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onTrackChanged = () => {
      setSelection(readSelection());
      dismissCallout();
    };
    window.addEventListener("track-changed", onTrackChanged);
    return () => window.removeEventListener("track-changed", onTrackChanged);
  }, []);

  // Publish the navbar's measured height as a CSS variable so other sticky
  // headers (e.g. the SearchView criteria bar) can offset themselves below it.
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const publish = () => {
      const h = el.getBoundingClientRect().height;
      document.documentElement.style.setProperty("--navbar-height", `${h}px`);
    };
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [menuOpen]);

  useEffect(() => {
    if (!helpOpen) return;
    const onDocClick = (e) => {
      if (helpRef.current && !helpRef.current.contains(e.target)) {
        setHelpOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [helpOpen]);

  const launchTour = () => {
    const tourKey = TOUR_KEY_BY_PATH[location.pathname];
    if (tourKey) {
      window.dispatchEvent(new CustomEvent('start-tour', { detail: tourKey }));
    }
    setHelpOpen(false);
  };

  const [tabWarningIndex, setTabWarningIndex] = useState(null);

  const goToTab = (path, index) => {
    if (!trackName) {
      setTabWarningIndex(index);
      setTimeout(() => setTabWarningIndex(null), 3000);
      return;
    }
    if (location.pathname !== path) navigate(path);
  };

  const openSelector = () => {
    dismissCallout();
    navigate("/editTrackEvent", { state: { from: location.pathname } });
    setMenuOpen(false);
  };

  const handleLogout = () => {
    setMenuOpen(false);
    logout();
  };

  const trackName = selection?.trackName;
  const eventName = selection?.eventName;
  const contextLabel = trackName
    ? eventName
      ? `${trackName} · ${eventName}`
      : trackName
    : "Select track";

  const showLiveDot = connectionStatus === "connected";

  return (
    <nav className="navbar" ref={navRef}>
      <div className="navbar-row navbar-row--context">
        <button
          type="button"
          className="navbar-context-chip"
          onClick={openSelector}
          title="Change track or event"
        >
          <span className="navbar-context-label">{contextLabel}</span>
          <span className="navbar-context-caret">▾</span>
        </button>

        <div className="navbar-help" ref={helpRef}>
          <button
            type="button"
            className="navbar-help-button"
            onClick={() => setHelpOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={helpOpen}
            aria-label="Help"
            title="Help"
          >
            <FaQuestionCircle size={18} />
          </button>
          {helpOpen && (
            <div className="navbar-help-menu" role="menu">
              {TOUR_KEY_BY_PATH[location.pathname] && (
                <button type="button" role="menuitem" onClick={launchTour}>
                  <FaMapSigns size={12} />
                  <span>Take the Tour</span>
                </button>
              )}
              <a
                role="menuitem"
                href="https://docs.1320cc.com"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setHelpOpen(false)}
              >
                <FaBook size={12} />
                <span>View Documentation</span>
              </a>
            </div>
          )}
        </div>

        <div className="navbar-user" ref={menuRef}>
          {/* {user && (
            <span className="user-name">
              {user.firstName} {user.lastName}
            </span>
          )} */}
          <NotificationBell />
          <a
            className="navbar-feedback-button"
            href="mailto:feedback@1320cc.com?subject=Early%20Access%20Feedback"
            onClick={() => setMenuOpen(false)}
          >
            <FaEnvelope size={15} />
            <span>Feedback</span>
          </a>
          <button
            type="button"
            className="navbar-user-button"
            onClick={() => setMenuOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label="User menu"
          >
            <FaUserCircle size={20} />
          </button>
          {menuOpen && (
            <div className="navbar-user-menu" role="menu">
              {user && (user.firstName || user.lastName || user.email) && (
                <div className="navbar-user-menu-header" role="presentation">
                  {(user.firstName || user.lastName) && (
                    <div className="navbar-user-menu-name">
                      {user.firstName} {user.lastName}
                    </div>
                  )}
                  {user.email && (
                    <div className="navbar-user-menu-email">{user.email}</div>
                  )}
                </div>
              )}
              <button type="button" role="menuitem" onClick={openSelector}>
                <FaPencilAlt size={12} />
                <span>Change track / event</span>
              </button>
              {/* <a
                role="menuitem"
                href="mailto:feedback@1320cc.com?subject=Early%20Access%20Feedback"
                onClick={() => setMenuOpen(false)}
              >
                <FaEnvelope size={12} />
                <span>Send feedback</span>
              </a> */}
              <button type="button" role="menuitem" onClick={handleLogout}>
                <FaSignOutAlt size={12} />
                <span>Sign out</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="navbar-tabs-wrap">
        <div className="navbar-row navbar-row--tabs" role="tablist">
          {TABS.map((tab, index) => {
            const active = location.pathname === tab.path;
            return (
              <button
                key={tab.path}
                type="button"
                role="tab"
                aria-selected={active}
                className={`navbar-tab${active ? " navbar-tab--active" : ""}`}
                onClick={() => goToTab(tab.path, index)}
              >
                {tab.label === "Live" && showLiveDot && (
                  <span className="navbar-tab-dot live-connected" />
                )}
                {tab.label}
              </button>
            );
          })}
        </div>
        {tabWarningIndex !== null && (
          <div
            className="navbar-tab-warning"
            data-pos={tabWarningIndex === 0 ? "start" : tabWarningIndex === TABS.length - 1 ? "end" : "center"}
          >
            <span className="navbar-tab-warning-arrow" />
            Please select a track &amp; event
          </div>
        )}
      </div>
    </nav>
  );
};

export default NavBar;
